"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CapabilityMatrix = exports.CapabilityMatrixPlayer = exports.CapabilityMatrixVersion = exports.ComposerVersionPayload = exports.CanonicalProgramView = exports.ProgramUpsert = exports.ProgramItemUpsert = exports.ComposerBackgroundAudioPatch = exports.ComposerCreate = exports.ComposerSummary = exports.ProgramSummary = exports.ProgramItemSummary = exports.ReplaceZoneItemsInput = exports.ProgramItemBinding = exports.Transition = exports.TransitionEffects = exports.LayoutUpdate = exports.LayoutCreate = exports.Layout = exports.Zone = exports.ZonePlaybackHints = exports.ZoneConstraints = void 0;
const zod_1 = require("zod");
const enums_js_1 = require("./enums.js");
const resource_schema_js_1 = require("./resource.schema.js");
/**
 * EDITORIAL LIMITS — what the operator may put in a zone. Enforced by the
 * admin editor's properties panel and re-checked server-side on program
 * save. (P2 spec §3.2; reconciliation task #38.)
 */
exports.ZoneConstraints = zod_1.z.object({
    min_w: zod_1.z.number().int().positive().optional(),
    min_h: zod_1.z.number().int().positive().optional(),
    allowed_resource_kinds: zod_1.z.array(zod_1.z.enum(enums_js_1.ResourceKinds)).min(1).optional(),
});
/**
 * RUNTIME REQUIREMENTS — what the *player* needs to render a zone
 * correctly. Informational in P2 (the PWA renderer reads `audio_capable`
 * to decide `<video muted>`); P3 matches `decode_class` against the
 * player's hardware capability flags. (P2 spec §3.2; reconciliation
 * task #38 — split out of the former single `constraints` block.)
 */
exports.ZonePlaybackHints = zod_1.z.object({
    audio_capable: zod_1.z.boolean().optional(),
    decode_class: zod_1.z.enum(['4k', '1080p', '720p', 'sd']).optional(),
    refresh_rate_hint_hz: zod_1.z.number().int().positive().optional(),
});
exports.Zone = zod_1.z.object({
    id: zod_1.z.string().min(1),
    x: zod_1.z.number().int().nonnegative(),
    y: zod_1.z.number().int().nonnegative(),
    w: zod_1.z.number().int().positive(),
    h: zod_1.z.number().int().positive(),
    kind: zod_1.z.enum(enums_js_1.ZoneKinds).default('media'),
    constraints: exports.ZoneConstraints.optional(),
    playback_hints: exports.ZonePlaybackHints.optional(),
});
// Shared fit refinement: each zone is fully inside [0, width_px) x [0, height_px).
// With x,y >= 0 and w,h > 0, the condition is x + w <= width_px and y + h <= height_px.
// Also: zone ids must be unique within a layout.
function applyLayoutRefinements(layout, ctx) {
    const seen = new Set();
    layout.zones.forEach((zone, idx) => {
        if (seen.has(zone.id)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['zones', idx, 'id'],
                message: `Duplicate zone id "${zone.id}"`,
            });
        }
        else {
            seen.add(zone.id);
        }
        if (zone.x + zone.w > layout.width_px) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['zones', idx],
                message: `Zone "${zone.id}" exceeds layout width (${zone.x} + ${zone.w} > ${layout.width_px})`,
            });
        }
        if (zone.y + zone.h > layout.height_px) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['zones', idx],
                message: `Zone "${zone.id}" exceeds layout height (${zone.y} + ${zone.h} > ${layout.height_px})`,
            });
        }
    });
}
exports.Layout = zod_1.z
    .object({
    id: zod_1.z.string().uuid(),
    width_px: zod_1.z.number().int().positive(),
    height_px: zod_1.z.number().int().positive(),
    zones: zod_1.z.array(exports.Zone).min(1),
})
    .superRefine(applyLayoutRefinements);
exports.LayoutCreate = zod_1.z
    .object({
    width_px: zod_1.z.number().int().positive(),
    height_px: zod_1.z.number().int().positive(),
    zones: zod_1.z.array(exports.Zone).min(1),
})
    .superRefine(applyLayoutRefinements);
exports.LayoutUpdate = zod_1.z
    .object({
    width_px: zod_1.z.number().int().positive(),
    height_px: zod_1.z.number().int().positive(),
    zones: zod_1.z.array(exports.Zone).min(1),
})
    .superRefine(applyLayoutRefinements);
// Loop transition effects — the animation used when an item ENTERS (the
// boundary from item N to N+1 uses N+1's resolved transition). Applied by the
// player; configurable as a loop default + per-item override.
exports.TransitionEffects = [
    'cut',
    'fade',
    'slide_left',
    'slide_right',
    'slide_up',
    'slide_down',
    'zoom_in',
    'zoom_out',
    'wipe_left',
    'wipe_right',
    'wipe_up',
    'wipe_down',
    'flip_x',
    'flip_y',
];
exports.Transition = zod_1.z.object({
    effect: zod_1.z.enum(exports.TransitionEffects),
    // Clamped [100, 2000] ms; default 500. The player + server both enforce.
    duration_ms: zod_1.z.number().int().min(100).max(2000).default(500),
});
// W8 T2 — discriminated union for PATCH input.
// id, order_idx, duration_ms are optional on input; the server preserves
// existing values on UPDATE and assigns defaults on CREATE.
// zone_id is required (the server uses it as the upsert key).
const programItemBase = {
    id: zod_1.z.string().uuid().optional(),
    // zone_id is any non-empty string (not UUID-constrained): P1 uses 'full';
    // multi-zone layouts use UUIDs (but the schema is permissive like ProgramItemUpsert).
    zone_id: zod_1.z.string().min(1),
    order_idx: zod_1.z.number().int().nonnegative().optional(),
    duration_ms: zod_1.z.number().int().positive().optional(),
    // Optional per-item transition override; omitted ⇒ inherit the loop default.
    transition: exports.Transition.optional(),
};
exports.ProgramItemBinding = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ ...programItemBase, kind: zod_1.z.literal('image'), resource_version_id: zod_1.z.string().uuid() }),
    zod_1.z.object({ ...programItemBase, kind: zod_1.z.literal('video'), resource_version_id: zod_1.z.string().uuid(), poster_resource_version_id: zod_1.z.string().uuid().nullable().optional() }),
    zod_1.z.object({ ...programItemBase, kind: zod_1.z.literal('web_page'), source_url: zod_1.z.string().url() }),
    zod_1.z.object({ ...programItemBase, kind: zod_1.z.literal('rss'), source_url: zod_1.z.string().url() }),
    zod_1.z.object({ ...programItemBase, kind: zod_1.z.literal('composer'), composer_id: zod_1.z.string().uuid() }),
    zod_1.z.object({ ...programItemBase, kind: zod_1.z.literal('empty') }),
]);
// Feature #8 — multi-item playlist editor.
// A bare array of bindings; the server replaces all items for the given zone.
// Empty array is explicitly allowed (clears the zone).
exports.ReplaceZoneItemsInput = zod_1.z.array(exports.ProgramItemBinding);
// W8 T2 — output shape: same discriminated union with id/order_idx/duration_ms
// required (server always populates them on stored items).
const programItemSummaryBase = {
    id: zod_1.z.string().uuid(),
    zone_id: zod_1.z.string().min(1),
    order_idx: zod_1.z.number().int().nonnegative(),
    duration_ms: zod_1.z.number().int().nonnegative(),
    transition: exports.Transition.optional(),
};
exports.ProgramItemSummary = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ ...programItemSummaryBase, kind: zod_1.z.literal('image'), resource_version_id: zod_1.z.string().uuid() }),
    zod_1.z.object({ ...programItemSummaryBase, kind: zod_1.z.literal('video'), resource_version_id: zod_1.z.string().uuid(), poster_resource_version_id: zod_1.z.string().uuid().nullable().optional() }),
    zod_1.z.object({ ...programItemSummaryBase, kind: zod_1.z.literal('web_page'), source_url: zod_1.z.string().url() }),
    zod_1.z.object({ ...programItemSummaryBase, kind: zod_1.z.literal('rss'), source_url: zod_1.z.string().url() }),
    zod_1.z.object({ ...programItemSummaryBase, kind: zod_1.z.literal('composer'), composer_id: zod_1.z.string().uuid() }),
    zod_1.z.object({ ...programItemSummaryBase, kind: zod_1.z.literal('empty') }),
]);
exports.ProgramSummary = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    order_idx: zod_1.z.number().int().nonnegative(),
    layout_id: zod_1.z.string().uuid(),
    duration_ms: zod_1.z.number().int().nonnegative().nullable(),
    // Loop default transition (per program); per-item overrides live on items.
    default_transition: exports.Transition.optional(),
    items: zod_1.z.array(exports.ProgramItemSummary),
});
exports.ComposerSummary = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    name_i18n: resource_schema_js_1.NameI18n,
    kind: zod_1.z.enum(enums_js_1.ComposerKinds),
    state: zod_1.z.enum(enums_js_1.ResourceStates),
    current_version_id: zod_1.z.string().uuid().nullable(),
    // Optional composition-level background-music track (an approved `audio`
    // resource version), carried by the current composer version. null = none.
    background_audio_resource_version_id: zod_1.z.string().uuid().nullable().optional(),
    created_at: zod_1.z.string().datetime(),
});
exports.ComposerCreate = zod_1.z.object({
    name_i18n: resource_schema_js_1.NameI18n,
    kind: zod_1.z.enum(enums_js_1.ComposerKinds).default('loop'),
    // P3 §6.2: optional minimum capability flags the player must satisfy
    // to play this composer's initial version. null/omitted = the
    // default/universal profile (canonical key '' — see
    // composers.audit.service.ts capabilityProfileKey). Free-form
    // string[] wire format; CAPABILITY_FLAGS is the curated vocabulary.
    min_capability_flags: zod_1.z.array(zod_1.z.string()).nullable().optional(),
    // Optional composition-level background music — an approved `audio` resource
    // version id. Validated server-side (must be kind=audio + state=approved).
    background_audio_resource_version_id: zod_1.z.string().uuid().nullable().optional(),
});
// Patch body for setting/clearing the composer's background music. Producing a
// new (draft) composer version that re-enters the 4-eyes approval flow.
exports.ComposerBackgroundAudioPatch = zod_1.z.object({
    background_audio_resource_version_id: zod_1.z.string().uuid().nullable(),
});
// `zone_id` is required (the field must be present in every persisted item) and
// must match a zone id on the program's layout. The referential check is enforced
// server-side; here we keep the P1-compatible default of 'full' so callers may
// omit it (zod will fill it) — the singleton P1 layout's only zone has id 'full'.
exports.ProgramItemUpsert = zod_1.z.object({
    order_idx: zod_1.z.number().int().nonnegative(),
    resource_version_id: zod_1.z.string().uuid(),
    duration_ms: zod_1.z.number().int().nonnegative(),
    zone_id: zod_1.z.string().min(1).default('full'),
});
exports.ProgramUpsert = zod_1.z.object({
    order_idx: zod_1.z.number().int().nonnegative(),
    layout_id: zod_1.z.string().uuid(),
    // Loop default transition (the per-loop setting). Optional; the server
    // persists it on the program + signs it into the canonical view. Per-item
    // overrides live on ProgramItemUpsert.transition.
    default_transition: exports.Transition.optional(),
    items: zod_1.z.array(exports.ProgramItemUpsert).min(1),
});
// CanonicalProgramView — the EXACT id-free structure the server hashes
// into the composer manifest's `programs_digest` (see
// apps/server/src/composers/canonical-programs.ts). Shipped in the
// player payload as `programs_canonical` so the PWA can JCS-hash it
// with @displaycms/crypto and compare to the signed digest + verify
// the composer Ed25519 signature (P2 Option B — signed zone geometry).
// `layout` carries the signed geometry; zones are the canonical Zone[]
// in stored array order (z-index per spec §3.2 — never reordered).
exports.CanonicalProgramView = zod_1.z.object({
    order_idx: zod_1.z.number().int().nonnegative(),
    layout_id: zod_1.z.string().uuid(),
    layout: zod_1.z.object({
        width_px: zod_1.z.number().int().nonnegative(),
        height_px: zod_1.z.number().int().nonnegative(),
        zones: zod_1.z.array(exports.Zone),
    }),
    // Loop default transition for this program — applies to every item unless the
    // item overrides it. OPTIONAL → JCS omits it when absent, so programs_digest
    // is byte-identical for compositions without transitions (DS §5).
    default_transition: exports.Transition.optional(),
    items: zod_1.z.array(zod_1.z.object({
        order_idx: zod_1.z.number().int().nonnegative(),
        resource_version_id: zod_1.z.string().uuid(),
        duration_ms: zod_1.z.number().int().nonnegative(),
        zone_id: zod_1.z.string().min(1),
        // Optional per-item override (same byte-identity rationale).
        transition: exports.Transition.optional(),
    })),
});
exports.ComposerVersionPayload = zod_1.z.object({
    composer_version_id: zod_1.z.string().uuid(),
    // The signed canonical view (Option B). Present on every P2 payload;
    // the player verifies sha256(jcs(programs_canonical)) ===
    // composer_manifest.canonical_json.programs_digest before rendering.
    programs_canonical: zod_1.z.array(exports.CanonicalProgramView),
    programs: zod_1.z.array(exports.ProgramSummary),
    composer_manifest: zod_1.z.object({
        canonical_json: zod_1.z.string(),
        signature_b64: zod_1.z.string(),
        signing_key_id: zod_1.z.string().uuid(),
    }),
    min_capability_flags: zod_1.z.array(zod_1.z.string()).nullable().optional(),
    // W7 T4 — true when the resolved composer has kind 'queue_overlay'.
    // The player runtime reads this flag to decide whether to connect to
    // /ws/queue for the ticket-calling real-time overlay (spec §4.3).
    // Optional for backwards-compat with old players that don't read the field.
    is_queue_overlay: zod_1.z.boolean().optional(),
    // Optional composition-level background music. The player fetches the bytes
    // via the normal device-JWT asset path (/assets/:resource_version_id),
    // verifies its own approval manifest, and plays it as a looping ambient
    // track exempt from the monoaudio rule. `sha256` keys the <audio> element so
    // a re-signed track swaps cleanly. Optional → old players ignore it.
    background_audio: zod_1.z
        .object({ resource_version_id: zod_1.z.string().uuid(), sha256: zod_1.z.string() })
        .optional(),
});
// P3 §6.2 / §8.5 — admin capability matrix. The server-computed
// answer to "for this composer, which co-active approved+signed
// version would each assigned player actually be served?". The
// per-player `resolved_version_id` is computed by REUSING the live
// resolver path (assignments.service.ts selectCapableVersionId
// predicate + legacy currentVersionId fallback +
// capability-resolver.ts resolveCapableVersion) so it can NEVER
// disagree with what resolveAssignment serves.
exports.CapabilityMatrixVersion = zod_1.z.object({
    composer_version_id: zod_1.z.string().uuid(),
    version_number: zod_1.z.number().int(),
    min_capability_flags: zod_1.z.array(zod_1.z.string()).nullable(),
    is_default_profile: zod_1.z.boolean(),
});
exports.CapabilityMatrixPlayer = zod_1.z.object({
    player_id: zod_1.z.string().uuid(),
    display_name: zod_1.z.string(),
    player_class: zod_1.z.enum(enums_js_1.PlayerClasses).nullable(),
    capability_flags: zod_1.z.array(zod_1.z.string()),
    resolved_version_id: zod_1.z.string().uuid().nullable(),
});
exports.CapabilityMatrix = zod_1.z.object({
    composer_id: zod_1.z.string().uuid(),
    versions: zod_1.z.array(exports.CapabilityMatrixVersion),
    players: zod_1.z.array(exports.CapabilityMatrixPlayer),
});
//# sourceMappingURL=composer.schema.js.map